import { Component, computed, effect, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import { Subject, finalize, switchMap } from 'rxjs';
import _ from 'lodash';
import { SigningAuthenticatorProxyService } from '../../../proxies/signing-authenticator.proxy.service';
import { SigningSessionStateService } from '../../../services/signing-session-state.service';
import { ToastService } from '@app/services/toast.service';
import { UserProxyService } from '../../../proxies/user.proxy.service';
import { User } from '../../../models/user-manager/user';
import { createDynamicFilterState, DynamicFilterConfig, DynamicFilterValues, FilterOption, ModalService } from '@card-software/library';
import { AuthService, SiteService } from '@card-software/nexus-utilities';
import { Group } from '../../../models/user-manager/group';
import { GroupProxyService } from '../../../proxies/group.proxy.service';
import { SiteProxyService } from '../../../proxies/site.proxy.service';
import { UserModalComponent } from '../../modals/user-modal/user-modal.component';
import { UserManagerAdminGroup } from '../../../models/authorization/enum/user-group.enum';
import { isDelegatedUserManager } from '../../../utilities/delegate-role.util';
import { Site } from '@card-software/nexus-utilities';
import { environment } from '@environments/environment';
@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrl: './users.component.scss',
})
export class UsersComponent implements OnInit, OnDestroy {
  listUsers = signal<User[] | null>(null);
  /** Null until loaded: the group filter needs its options. */
  private readonly groups = signal<Group[] | null>(null);
  isLoading = 0;
  filterConfig = signal<DynamicFilterConfig | null>(null);
  readonly filterState = createDynamicFilterState({
    storageKey: 'users-filters',
    config: this.filterConfig,
  });
  canAssignSites = computed(() => AuthService.hasRole(UserManagerAdminGroup.UserSiteAssigner));
  /** Delegated user manager (#6263): the API lists only the users they manage; no authenticator reset. */
  readonly isDelegate = computed(() => isDelegatedUserManager());
  /** The Sites column: assigners, and delegates (it shows their active site). */
  readonly showSites = computed(() => this.canAssignSites() || this.isDelegate());
  /** Reset authenticator belongs to the e-signature feature: hidden where it is off (prod). */
  readonly canResetAuthenticator = computed(() => environment.features.signature && !this.isDelegate());
  private useAllSitesInSearch = AuthService.hasGlobalRole(UserManagerAdminGroup.UserSiteAssigner);
  // Full site catalog (loaded for assigners) so names resolve even for sites the
  // logged-in user doesn't belong to; falls back to the user's own sites.
  private allSites: Site[] = [];

  getAssignedSiteNames(user: User): string {
    if (!user.siteIds?.length) return '';
    const sites = this.allSites.length > 0 ? this.allSites : SiteService.getSites();
    return user.siteIds
      .map((id) => sites.find((s) => s.id === id)?.name ?? '')
      .filter((n) => n)
      .join(', ');
  }

  private nameFilter = '';
  private groupIdFilter: string[] = [];
  private search$ = new Subject<void>();

  private readonly authenticatorProxy = inject(SigningAuthenticatorProxyService);
  private readonly signingState = inject(SigningSessionStateService);
  private readonly translate = inject(TranslateService);
  private readonly toast = inject(ToastService);
  readonly resetTarget = signal<string | null>(null);
  readonly resetting = signal(false);

  constructor(
    private userProxyService: UserProxyService,
    private groupProxyService: GroupProxyService,
    private siteProxyService: SiteProxyService,
    private modalService: ModalService,
  ) {
    this.filterState.applied$.pipe(takeUntilDestroyed()).subscribe((filters) => this.searchWithFilters(filters));

    effect(
      () => {
        const groups = this.groups();
        if (groups && this.translate.currentLang) this.initializeFilterConfig(groups);
      },
      { allowSignalWrites: true },
    );
  }

  ngOnInit(): void {
    this.groupProxyService.getAllGroups().subscribe({
      next: (res) => this.groups.set(res.data ?? []),
      error: (error) => {
        this.groups.set([]);
        throw error;
      },
    });

    if (this.canAssignSites()) {
      this.siteProxyService.getAllSites().subscribe((res) => {
        if (res.success && res.data) this.allSites = res.data;
      });
    }

    this.search$
      .pipe(
        switchMap(() => {
          this.isLoading++;
          return this.userProxyService.searchUsers(
            this.nameFilter || undefined,
            this.groupIdFilter.length > 0 ? this.groupIdFilter : undefined,
            this.useAllSitesInSearch,
          );
        }),
      )
      .subscribe({
        next: (response) => {
          this.listUsers.set(response.data ?? []);
          this.isLoading--;
        },
        error: (error) => {
          this.isLoading--;
          throw error;
        },
      });
  }

  ngOnDestroy(): void {
    this.search$.complete();
  }

  private translateOr(key: string, fallback: string): string {
    const translation = this.translate.instant(key);
    return translation === key ? fallback : translation;
  }

  private initializeFilterConfig(groups: Group[]): void {
    const groupOptions: FilterOption[] = _.sortBy(
      groups.filter((g) => g.id),
      (g) => (g.name ?? '').toLowerCase(),
    ).map((g) => ({ label: g.name ?? '', value: g.id! }));

    this.filterConfig.set({
      persistInUrl: true,
      persistVisibility: true,
      filters: [
        {
          type: 'text',
          formControlName: 'username',
          label: this.translateOr('Users_Filter_Username', 'Username'),
          placeholder: this.translateOr('Users_Filter_Username_Placeholder', 'Search user'),
          defaultVisible: true,
        },
        {
          type: 'multiselect',
          formControlName: 'groupIds',
          label: this.translateOr('Users_Filter_Groups', 'Groups'),
          options: groupOptions,
          showClear: true,
          defaultVisible: true,
        },
      ],
    });
  }

  private searchWithFilters(filters: DynamicFilterValues): void {
    const username = filters['username'];
    const groupIds = filters['groupIds'];
    this.nameFilter = typeof username === 'string' ? username.trim() : '';
    this.groupIdFilter = Array.isArray(groupIds) ? groupIds.map((v) => String(_.isObject(v) ? (v as FilterOption).value : v)) : [];
    this.search$.next();
  }

  confirmReset() {
    const username = this.resetTarget();
    if (!username || this.resetting()) return;

    this.resetting.set(true);
    this.authenticatorProxy
      .reset(username)
      .pipe(finalize(() => this.resetting.set(false)))
      .subscribe({
        next: () => {
          this.resetTarget.set(null);
          this.toast.success(this.translate.instant('Common_Success'), this.translate.instant('Users_ResetAuthenticator_Done'));
        },
        error: (error) => this.toast.error(this.translate.instant('Signing_Error_Title'), this.signingState.messageFrom(error)),
      });
  }

  openModalUser(user: User | null) {
    // A delegate opens the user as the API sees it now: if it is no longer theirs (404), say so and refresh.
    if (user?.id && this.isDelegate()) {
      this.isLoading++;
      this.userProxyService
        .getUserById(user.id, true)
        .pipe(finalize(() => this.isLoading--))
        .subscribe({
          next: (response) => this.showUserModal(response.data ?? user),
          error: (error: unknown) => {
            if (error instanceof HttpErrorResponse && error.status === 404) {
              this.toast.warn(
                this.translate.instant('Users_NotAvailable_Title'),
                this.translate.instant('Users_NotAvailable_Detail_Open'),
                6000,
              );
            } else {
              this.toast.error(this.translate.instant('Common_Error'), this.translate.instant('Users_Load_Error'));
            }
            this.search$.next();
          },
        });
      return;
    }

    this.showUserModal(user);
  }

  private showUserModal(user: User | null) {
    this.modalService
      .open(
        UserModalComponent,
        { modalClass: 'modal_large' },
        {
          user,
        },
      )
      .subscribe(() => {
        this.search$.next();
      });
  }
}
